import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckSquare, Plus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api, errMsg, fmtDateShort, TASK_STATUS } from "../lib/api";
import { EmptyState, PageHeader, PriorityBadge, TaskStatusBadge } from "../components/ui-bits";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { TaskForm } from "../components/TaskForm";

export default function Tasks() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState("mine");
  const [statusFilter, setStatusFilter] = useState("open");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (scope === "mine") params.mine = true;
      if (statusFilter === "open") params.status = "todo,in_arbeit,wartend";
      else if (statusFilter !== "all") params.status = statusFilter;
      const { data } = await api.get("/tasks", { params });
      setTasks(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [scope, statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const [p, u] = await Promise.all([api.get("/projects"), api.get("/users")]);
        setProjects(p.data);
        setUsers(u.data);
      } catch {}
    })();
  }, []);

  const toggle = async (t) => {
    try {
      await api.patch(`/tasks/${t.id}/status`, { status: t.status === "fertig" ? "todo" : "fertig" });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const grouped = tasks.reduce((acc, t) => {
    const key = `${t.client_name || "—"} · ${t.project_name || "Ohne Projekt"}`;
    (acc[key] = acc[key] || []).push(t);
    return acc;
  }, {});

  return (
    <div className="animate-fade-up" data-testid="tasks-page">
      <PageHeader overline="Arbeitsplan" title="Aufgaben" testid="tasks-header">
        <Select value={scope} onValueChange={setScope}>
          <SelectTrigger data-testid="tasks-scope" className="h-9 w-44 rounded-xl bg-white text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="mine">Meine Aufgaben</SelectItem>
            <SelectItem value="all">Alle Aufgaben</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="tasks-status-filter" className="h-9 w-40 rounded-xl bg-white text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="open">Offen</SelectItem>
            {Object.entries(TASK_STATUS).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v.label}</SelectItem>
            ))}
            <SelectItem value="all">Alle</SelectItem>
          </SelectContent>
        </Select>
        <Button data-testid="new-task-button" onClick={() => { setEditing(null); setFormOpen(true); }} className="rounded-xl bg-[#B88A4A] text-white hover:bg-[#a67a3f]">
          <Plus className="mr-1.5 h-4 w-4" /> Neue Aufgabe
        </Button>
      </PageHeader>

      {loading ? (
        <div className="h-64 animate-pulse rounded-2xl bg-white" />
      ) : tasks.length === 0 ? (
        <EmptyState icon={CheckSquare} title="Keine Aufgaben" hint={scope === "mine" ? "Dir sind aktuell keine Aufgaben zugewiesen." : "Erstelle eine neue Aufgabe in einem Projekt."} testid="tasks-empty">
          <Button data-testid="empty-new-task-button" onClick={() => { setEditing(null); setFormOpen(true); }} className="rounded-xl bg-[#B88A4A] text-white hover:bg-[#a67a3f]">
            <Plus className="mr-1.5 h-4 w-4" /> Aufgabe erstellen
          </Button>
        </EmptyState>
      ) : (
        <div className="space-y-5">
          {Object.entries(grouped).map(([group, list]) => (
            <div key={group}>
              <p className="mb-2 text-xs font-semibold uppercase tracking-[0.15em] text-[#B88A4A]">{group}</p>
              <div className="overflow-hidden rounded-2xl border border-[#1D225C]/10 bg-white shadow-sm">
                {list.map((t, i) => (
                  <div key={t.id} data-testid={`task-item-${t.id}`} className={`flex flex-wrap items-center gap-3 px-4 py-3 ${i ? "border-t border-[#1D225C]/5" : ""}`}>
                    <button
                      data-testid={`task-toggle-${t.id}`}
                      onClick={() => toggle(t)}
                      className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border-2 transition-colors ${
                        t.status === "fertig" ? "border-[#22C55E] bg-[#22C55E] text-white" : "border-[#1D225C]/20 hover:border-[#22C55E]"
                      }`}
                    >
                      {t.status === "fertig" && <CheckSquare className="h-3.5 w-3.5" />}
                    </button>
                    <button onClick={() => navigate(`/projekte/${t.project_id}?tab=aufgaben`)} className="min-w-0 flex-1 text-left">
                      <p className={`text-sm font-medium ${t.status === "fertig" ? "text-[#999] line-through" : "text-[#1D225C]"}`}>{t.title}</p>
                      <p className="text-xs text-[#777]">
                        {t.assignee_name || "Niemand"}{t.due_date ? ` · fällig ${fmtDateShort(t.due_date)}` : ""}
                      </p>
                    </button>
                    <PriorityBadge priority={t.priority} />
                    <TaskStatusBadge status={t.status} testid={`task-status-badge-${t.id}`} />
                    <button data-testid={`task-edit-${t.id}`} onClick={() => { setEditing(t); setFormOpen(true); }} className="rounded-lg px-2 py-1 text-xs font-medium text-[#B88A4A] hover:bg-[#B88A4A]/10">
                      Bearbeiten
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <TaskForm open={formOpen} onOpenChange={setFormOpen} task={editing} projects={projects} users={users} onSaved={load} />
    </div>
  );
}
